import { useState } from 'react'
import type { Profile } from '../../lib/metrics'
import { ACTIVITIES, ACTIVITY_BY_KEY } from '../../lib/energy'
import {
  DEFAULT_SETTINGS,
  KIND_LABEL,
  WEEKDAY_NAMES,
  planForDate,
  weekSummary,
  type ActivityKind,
  type ScheduleSettings,
} from '../../lib/schedule'
import { useClearOverride, useSaveLog, useSchedule, useSetOverride, useTodayLog } from '../../api/hooks'
import { todayIso, trDate } from '../../lib/format'

/**
 * Günün planı — şablon + istisnalardan TÜRETİLİR, sabit bir tablodan okunmaz.
 * "Bugün arkadaşlarla tenise gidiyorum" tek dokunuş; öğün saatleri, hatırlatıcılar
 * ve antrenman sırası kendiliğinden buna uyum sağlar.
 */
export function TodayCard({ profile }: { profile: Profile }) {
  const sched = useSchedule()
  const setOverride = useSetOverride()
  const clearOverride = useClearOverride()
  const log = useTodayLog()
  const saveLog = useSaveLog()
  const [time, setTime] = useState('20:30')
  const [sport, setSport] = useState('tennis_singles')

  const date = todayIso()

  if (sched.isLoading) return <div className="card"><p className="sub">Program yükleniyor…</p></div>
  if (!sched.data) return null

  const settings: ScheduleSettings = {
    ...DEFAULT_SETTINGS,
    strengthPerWeek: profile.strengthPerWeek ?? DEFAULT_SETTINGS.strengthPerWeek,
    makeupWeekday: profile.makeupWeekday ?? DEFAULT_SETTINGS.makeupWeekday,
    makeupTime: (profile.makeupTime ?? DEFAULT_SETTINGS.makeupTime).slice(0, 5),
    preTennisMinutes: profile.preTennisMinutes ?? DEFAULT_SETTINGS.preTennisMinutes,
    sessionMinutes: profile.sessionMinutes ?? DEFAULT_SETTINGS.sessionMinutes,
    postTennisMinutes: profile.postTennisMinutes ?? DEFAULT_SETTINGS.postTennisMinutes,
  }

  const base = { meal1: profile.meal1, meal2: profile.meal2, kitchenClose: profile.kitchenClose }
  const doneTotal = sched.data.strengthDoneDates.length

  const plan = planForDate(date, sched.data.template, sched.data.overrides, base, doneTotal, settings)
  const week = weekSummary(date, sched.data.template, sched.data.overrides, sched.data.strengthDoneDates, settings)

  const strengthDone = log.data?.strengthDone ?? false
  const toggleDone = (v: boolean) =>
    saveLog.mutate({
      checks: log.data?.checks ?? [],
      cycles: log.data?.cycles ?? 0,
      standSeconds: log.data?.standSeconds ?? 0,
      strengthDone: v,
      updatedAt: new Date().toISOString(),
    })

  // "Bugün ekstra spor" — hangi spor olduğunu da soruyoruz. Tenis varsayılan
  // ama tek seçenek değil; öğün penceresi her akşam sporunda aynı şekilde kayıyor.
  const goSport = () => setOverride.mutate({
    date, kind: 'sport', startTime: time, activityKey: sport,
  })
  const setKind = (kind: ActivityKind) =>
    setOverride.mutate({ date, kind, startTime: null, activityKey: null })

  return (
    <div className="card" style={{ borderLeft: `3px solid ${plan.kind === 'sport' ? 'var(--s2)' : 'var(--s1)'}` }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Bugün · {WEEKDAY_NAMES[plan.weekday]} {trDate(date)}
        {plan.isOverride && <> · <span style={{ color: 'var(--s2)' }}>program dışı</span></>}
      </div>
      <h2 style={{ marginTop: 4 }}>{plan.label}</h2>
      <p style={{ color: 'var(--ink-2)', margin: '6px 0 0', fontSize: 14 }}>{plan.detail}</p>

      <div className="grid g4" style={{ marginTop: 16 }}>
        <MealTile label="1. öğün" time={plan.meals.meal1} />
        <MealTile label="Spor öncesi ara" time={plan.meals.preSnack} muted={!plan.meals.preSnack} />
        <MealTile label="2. öğün" time={plan.meals.meal2} highlight={plan.kind === 'sport'} />
        <MealTile label="Mutfak kapanış" time={plan.meals.kitchenClose} />
      </div>

      {plan.kind === 'strength' && (
        <label className="chk" style={{ marginTop: 14 }}>
          <input type="checkbox" checked={strengthDone} onChange={(e) => toggleDone(e.target.checked)} />
          <span>
            <span className="t">Antrenman {plan.workout} yapıldı</span>
            <br />
            <span className="d">
              İşaretleyince sıradaki antrenman {plan.workout === 'A' ? 'B' : 'A'} olur —
              sıra takvimden değil, yaptıklarından ilerliyor
            </span>
          </span>
        </label>
      )}

      <h3>Bugün planı değiştir</h3>
      {!plan.isOverride ? (
        <div className="row">
          <div style={{ flex: '1 1 200px' }}>
            <label className="f">Ne yapacaksın</label>
            <select value={sport} onChange={(e) => setSport(e.target.value)}>
              {[...new Set(ACTIVITIES.map((a) => a.group))].map((g) => (
                <optgroup key={g} label={g}>
                  {ACTIVITIES.filter((a) => a.group === g).map((a) => (
                    <option key={a.key} value={a.key}>{a.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 0 120px' }}>
            <label className="f">Saat</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <button className="primary" onClick={goSport} disabled={setOverride.isPending}>
            Bugün {ACTIVITY_BY_KEY[sport]?.name ?? 'spor'} var
          </button>
          <button onClick={() => setKind('rest')}>Bugün dinleniyorum</button>
          {plan.kind !== 'strength' && <button onClick={() => setKind('strength')}>Kuvvet yapacağım</button>}
        </div>
      ) : (
        <div className="row">
          <span style={{ color: 'var(--ink-2)', fontSize: 14 }}>
            Bugün program dışı: <strong>{KIND_LABEL[plan.kind]}</strong>
            {plan.startTime && ` ${plan.startTime}`} (şablonda {KIND_LABEL[plan.plannedKind]} vardı)
          </span>
          <button onClick={() => clearOverride.mutate(date)} disabled={clearOverride.isPending}>
            Programa geri dön
          </button>
        </div>
      )}

      <div className="warnbox" style={{ borderLeftColor: week.unreachable ? 'var(--crit)' : 'var(--warn)' }}>
        <strong>Bu hafta kuvvet:</strong> {week.strengthCompleted}/{week.strengthTarget} tamam
        {week.strengthOwed > 0 && <> · {week.strengthOwed} seans kaldı</>}
        {week.makeupDate && (
          <>
            {' '}· Program {week.strengthPlanned} güne düştü, eksik antrenman{' '}
            <strong>{WEEKDAY_NAMES[settings.makeupWeekday]} {week.makeupTime}</strong> telafisine düşüyor.
          </>
        )}
        {week.unreachable && (
          <> · <strong style={{ color: 'var(--crit)' }}>Bu hafta {week.strengthTarget} seansa ulaşılamıyor</strong> —
            telafi günü de dolu. Sorun değil, haftaya toparlarsın; ama bilmen için yazıyorum.</>
        )}
      </div>
    </div>
  )
}

function MealTile({ label, time, muted, highlight }:
  { label: string; time: string | null; muted?: boolean; highlight?: boolean }) {
  return (
    <div className="tile" style={highlight ? { borderColor: 'var(--s2)' } : undefined}>
      <div className="lab">{label}</div>
      <div className="val" style={{ fontSize: 22, color: muted ? 'var(--muted)' : undefined }}>
        {time ?? '—'}
      </div>
    </div>
  )
}
